import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { jobsAPI } from '../services/api';
import { MEDIA_URL, API } from '../config';
import Sidebar from '../components/Sidebar';
import '../styles/theme.css';
import './Jobs.css';

function Jobs() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());

  useEffect(() => {
    fetchJobs();
    fetchApplied();
  }, []);

  const fetchApplied = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/applications/my-applications/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const ids = new Set((Array.isArray(data) ? data : data.applications || []).map(a => a.job_id));
        setAppliedJobIds(ids);
      }
    } catch {}
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await jobsAPI.getJobs({ search });
      setJobs(response.data || []);
    } catch { setJobs([]); }
    finally { setLoading(false); }
  };

  const filtered = jobs.filter(j => {
    const matchSearch = !search || j.title?.toLowerCase().includes(search.toLowerCase()) || j.company?.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || j.employment_type === filterType;
    const matchMode = !filterMode || j.work_mode === filterMode;
    return matchSearch && matchType && matchMode;
  });

  const getTypeBadge = (type) => {
    const map = { 'Full-time': 'badge-blue', 'Part-time': 'badge-orange', 'Contract': 'badge-purple', 'Internship': 'badge-green', 'Remote': 'badge-gray' };
    return map[type] || 'badge-gray';
  };

  return (
    <div className="portal-layout">
      <Sidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Browse Jobs</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme">
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="portal-content">
          <div className="page-header">
            <h1>Browse Jobs</h1>
            <p>{jobs.length} opportunities available in South Africa</p>
          </div>

          {/* Search & Filters */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="jobs-toolbar">
              <div className="jobs-search-wrap">
                <i className="fas fa-search jobs-search-icon"></i>
                <input
                  className="form-control jobs-search-input"
                  type="text"
                  placeholder="Search by title or company..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && fetchJobs()}
                />
              </div>
              <select className="form-control jobs-filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Internship</option>
              </select>
              <select className="form-control jobs-filter-select" value={filterMode} onChange={e => setFilterMode(e.target.value)}>
                <option value="">All Modes</option>
                <option>Remote</option>
                <option>Hybrid</option>
                <option>On-site</option>
              </select>
              <button className="btn btn-primary" onClick={fetchJobs}>
                <i className="fas fa-search"></i> Search
              </button>
            </div>
          </div>

          {/* Jobs Grid */}
          {loading ? (
            <div className="empty-state">
              <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
              <p>Loading jobs...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-briefcase"></i>
              <h3>No jobs found</h3>
              <p>Try adjusting your search filters</p>
            </div>
          ) : (
            <div className="jobs-grid">
              {filtered.map(job => (
                <div key={job.id} className={`job-card${job.is_featured ? ' job-card-featured' : ''}`}>
                  <div className="job-card-top">
                    <div
                      className={`job-company-logo${job.company_logo ? ' has-logo' : ''}`}
                      onClick={() => navigate(`/company/${encodeURIComponent(job.company)}`)}
                      style={{ cursor: 'pointer' }}
                      title={`View ${job.company} profile`}
                    >
                      {job.company_logo
                        ? <img src={job.company_logo.startsWith('http') ? job.company_logo : `${MEDIA_URL}${job.company_logo}`} alt={job.company} className="company-logo-img" />
                        : job.company?.charAt(0).toUpperCase()
                      }
                    </div>
                    <div className="job-card-meta">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <h3 className="job-card-title">{job.title}</h3>
                        {job.is_featured && (
                          <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: '0.7rem' }}>
                            <i className="fas fa-star"></i> Featured
                          </span>
                        )}
                        {appliedJobIds.has(job.id) && (
                          <span className="badge badge-green" style={{ fontSize: '0.7rem' }}>
                            <i className="fas fa-check"></i> Applied
                          </span>
                        )}
                      </div>
                      <div className="job-card-company">
                        <i className="fas fa-building"></i> {job.company}
                        {job.is_verified && (
                          <span title="Verified company" style={{ marginLeft: '0.35rem', color: '#22c55e', fontSize: '0.8rem' }}>
                            <i className="fas fa-check-circle"></i>
                          </span>
                        )}
                        {job.is_flagged && (
                          <span title="This listing has been flagged for review" style={{ marginLeft: '0.35rem', color: '#f59e0b', fontSize: '0.8rem' }}>
                            <i className="fas fa-exclamation-triangle"></i>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="job-card-tags">
                    <span className={`badge ${getTypeBadge(job.employment_type)}`}>
                      {job.employment_type || 'Full-time'}
                    </span>
                    {job.work_mode && (
                      <span className="badge badge-gray">{job.work_mode}</span>
                    )}
                    {job.experience_level && (
                      <span className="badge badge-purple">{job.experience_level}</span>
                    )}
                  </div>

                  <div className="job-card-details">
                    <span><i className="fas fa-map-marker-alt"></i> {job.city || job.location || 'South Africa'}</span>
                    {(job.salary_min || job.salary_max) && (
                      <span><i className="fas fa-money-bill-wave"></i> R{job.salary_min?.toLocaleString()} – R{job.salary_max?.toLocaleString()}</span>
                    )}
                    {job.application_deadline && (() => {
                      const days = Math.ceil((new Date(job.application_deadline) - new Date()) / 86400000);
                      const color = days <= 3 ? '#ef4444' : days <= 7 ? '#f59e0b' : '#10b981';
                      return (
                        <span style={{ color, fontWeight: 600 }}>
                          <i className="fas fa-clock"></i> {days <= 0 ? 'Closing today' : `Closes in ${days}d`}
                        </span>
                      );
                    })()}
                  </div>

                  {appliedJobIds.has(job.id) ? (
                    <button className="btn btn-sm job-card-btn"
                      style={{ background: 'var(--surface-2)', color: '#10b981', border: '1px solid #10b981', cursor: 'default' }}
                      onClick={() => navigate(`/my-applications`)}>
                      <i className="fas fa-check-circle"></i> Already Applied
                    </button>
                  ) : (
                    <button className="btn btn-primary btn-sm job-card-btn"
                      onClick={() => navigate(`/jobs/${job.id}`)}>
                      View Details <i className="fas fa-arrow-right"></i>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Jobs;
